import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, EMPTY } from 'rxjs';
import { map, switchMap, tap, shareReplay, filter } from 'rxjs/operators';
import { TranslationApiService } from './translation-api.service';
import { Language, TranslationSet } from '../models/translation.model';
import { TenantService } from './tenant.service';
import { GeneralService, GeneralLanguage } from './general';
import { convertThreeToTwoLetterCode, convertTwoToThreeLetterCode } from '../constants/language-codes';

@Injectable({
  providedIn: 'root'
})
export class DynamicTranslationService {
  private readonly STORAGE_KEY = 'selectedLanguage';
  private currentLanguageSubject = new BehaviorSubject<Language | null>(null);
  private translationsSubject = new BehaviorSubject<{ [key: string]: string }>({});
  private languagesSubject = new BehaviorSubject<Language[]>([]);

  public currentLanguage$ = this.currentLanguageSubject.asObservable();
  public translations$ = this.translationsSubject.asObservable();
  public languages$ = this.languagesSubject.asObservable();

  constructor(
    private translationApi: TranslationApiService,
    private tenantService: TenantService,
    private generalService: GeneralService
  ) {
    this.initializeService();
  }

  private initializeService(): void {
    // Load languages from database
    this.loadLanguages().subscribe();

    // Watch for language changes and load translations
    this.currentLanguage$.pipe(
      switchMap(language => {
        if (!language) return EMPTY;
        return this.loadTranslations(language.code);
      })
    ).subscribe();
  }

  loadLanguages(): Observable<Language[]> {
    // Use GeneralService to get languages instead of separate API call
    return this.generalService.languages$.pipe(
      // Only process when languages are actually available
      filter((generalLanguages: GeneralLanguage[]) => generalLanguages.length > 0),
      map((generalLanguages: GeneralLanguage[]) => {
        // Convert GeneralLanguage (3-letter codes) to Language (2-letter codes)
        return generalLanguages
          .filter(lang => lang.state === 'LIVE')
          .map((lang, index) => ({
            id: lang.uuid || `lang-${index}`,
            code: convertThreeToTwoLetterCode(lang.code),
            name: lang.name,
            nativeName: lang.name,
            flag: lang.flag,
            isActive: lang.state === 'LIVE',
            isDefault: lang.primaryLanguage || false
          }));
      }),
      tap(languages => {
        if (languages.length > 0) {
          this.languagesSubject.next(languages);

          // Set initial language
          if (!this.currentLanguageSubject.value) {
            const storedLangCode = this.getStoredLanguageCode();

            // Try to find language in this order:
            // 1. Stored language code from localStorage
            // 2. Language marked as default
            // 3. English (en)
            // 4. First available language
            let language = storedLangCode
              ? languages.find(l => l.code === storedLangCode)
              : undefined;

            if (!language) {
              language = languages.find(l => l.isDefault);
            }

            if (!language) {
              language = languages.find(l => l.code === 'en');
            }

            if (!language) {
              language = languages[0];
            }

            if (language) {
              this.setCurrentLanguage(language);
            }
          }
        }
      }),
      shareReplay(1)
    );
  }

  loadTranslations(languageCode: string, appType?: 'GS' | 'PORTAL' | null): Observable<TranslationSet> {
    // Convert 2-letter code to 3-letter code for API call
    const apiLanguageCode = convertTwoToThreeLetterCode(languageCode);

    return this.translationApi.getTranslations(apiLanguageCode, true, appType).pipe(
      tap(translationSet => {
        // Ensure we have valid translations object
        if (translationSet.translations && typeof translationSet.translations === 'object') {
          this.translationsSubject.next(translationSet.translations);
        } else {
          console.error('[DynamicTranslationService] Invalid translations format received:', translationSet);
        }
      })
    );
  }

  setCurrentLanguage(language: Language): void {
    this.currentLanguageSubject.next(language);
    localStorage.setItem(this.STORAGE_KEY, language.code);
  }

  getCurrentLanguage(): Language | null {
    return this.currentLanguageSubject.value;
  }

  getLanguages(): Language[] {
    return this.languagesSubject.value;
  }

  translate(key: string, params?: { [key: string]: any }): Observable<string> {
    return this.translations$.pipe(
      map(translations => {
        let translation = translations[key] || key;

        // Replace parameters if provided
        if (params) {
          Object.keys(params).forEach(paramKey => {
            const regex = new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g');
            translation = translation.replace(regex, params[paramKey]);
          });
        }

        return translation;
      })
    );
  }

  instant(key: string, params?: { [key: string]: any }): string {
    const translations = this.translationsSubject.value;

    let translation = translations[key] || key;

    // Replace parameters if provided
    if (params) {
      Object.keys(params).forEach(paramKey => {
        const regex = new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g');
        translation = translation.replace(regex, params[paramKey]);
      });
    }

    return translation;
  }

  updateTranslation(key: string, value: string): Observable<any> {
    const currentLang = this.currentLanguageSubject.value;
    if (!currentLang) {
      throw new Error('No language selected');
    }

    return this.translationApi.updateTranslation({
      key,
      value,
      languageCode: currentLang.code
    }).pipe(
      tap(() => {
        // Update local translations
        const currentTranslations = this.translationsSubject.value;
        currentTranslations[key] = value;
        this.translationsSubject.next({ ...currentTranslations });
      })
    );
  }

  addNewLanguage(language: Partial<Language>): Observable<Language> {
    return this.translationApi.createLanguage(language).pipe(
      tap(() => this.loadLanguages().subscribe())
    );
  }

  refreshTranslations(): void {
    const currentLang = this.currentLanguageSubject.value;
    if (currentLang) {
      const apiLanguageCode = convertTwoToThreeLetterCode(currentLang.code);
      this.translationApi.clearCache(apiLanguageCode);
      this.loadTranslations(currentLang.code).subscribe();
    }
  }

  /**
   * Clear all cached translations
   */
  clearAllCache(): void {
    this.translationApi.clearCache();
  }

  private getStoredLanguageCode(): string | null {
    return localStorage.getItem(this.STORAGE_KEY);
  }

  /**
   * Get the current tenant ID
   */
  getTenantId(): string {
    return this.tenantService.getTenantId();
  }

  /**
   * Get the current tenant name
   */
  getTenantName(): string {
    return this.tenantService.getTenantName();
  }

  // Utility method to get all translation keys
  getAllKeys(): Observable<string[]> {
    return this.translationApi.getTranslationKeys();
  }

  // Export translations for current language
  exportCurrentTranslations(): Observable<any> {
    const currentLang = this.currentLanguageSubject.value;
    if (!currentLang) {
      throw new Error('No language selected');
    }
    return this.translationApi.exportTranslations(currentLang.code);
  }

  getIso639Code(): string {
    const lang = this.currentLanguageSubject.value;
    return lang?.code ? convertTwoToThreeLetterCode(lang.code) : 'eng';
  }
}
